import { IdTokenClaims, User, UserManager } from "oidc-client-ts";
import { IIamSettings } from "../utils/auth/iamSettings";
import { getIamManager } from "../utils/auth/iamManager";
import DriveApi from "../utils/api";
import * as logger from "../utils/logger";
import { shouldAddExtraLogging } from "../utils/logger.internal";
import { DriveStore } from "../store/reducers";
import IamSilentSignInError from "../utils/auth/iamSilentSignInError";
import constants from "../constants";
import { getDatabaseNameFromUri } from "../utils/databaseNameParsingUtilities";
import { sendToErrorTracker } from "../utils/error";
import { ICredentialsWithServer } from "../utils/credentialsStore";
import { getItem } from "../utils/native/secureStorageApi";
import { IamCredentials } from "../store/iamCredentials/actions";
import { trackedFetch } from "../utils/trackedFetch";
import { _originalLocalStorage } from "../utils/storage/storage.base";

const IAM_CUSTOM_KEY_NAMESPACE = "https://geotab.com";
const MYADMIN_KEY = `${ IAM_CUSTOM_KEY_NAMESPACE }/myadminPermission`;

export function shouldLoginWithAppAuth() {
    return window.geotabModules?.auth && (typeof window.geotabModules.auth["login"] === "function");
}

export function shouldLoginWithAS() {
    return window.geotabModules?.sso && (typeof window.geotabModules.sso["samlLoginWithAS"] === "function");
}

export interface IIamAppState {
    database: string;
    server: string;
    addCoDriver: boolean;
}

export enum IamConstants {
    UsernameSearchParam = "username=",
    UrlHash = "#login,(iam:!t,loggingIn:!t)",
    UrlHashWithAddCoDriver = "#login,(addCoDriver:!t,iam:!t)"
}

export enum IamOptimisticRefreshStatus {
    PROCEED_TO_REFRESH,
    DO_NOT_REFRESH,
    KEYCLOAK_SERVER_ERROR_ENCOUNTERED
}

export interface IToken {
    accessToken: string;
    refreshToken: string | undefined;
    expiredAt: number | undefined; // Epoch time in seconds
}

export declare type PopupCallBackType = (url: string) => Promise<unknown> | void;

export interface IIamService {
    init(iIamSettings: IIamSettings, storageKey: string, signinPopupCallback?: PopupCallBackType): void;
    shouldOptimisticRefresh(creds: ICredentialsWithServer, id: string, refreshOnLaunch: boolean): Promise<IamOptimisticRefreshStatus>;
    getClaim(): Promise<IdTokenClaims | undefined>;
    getUser(iamSettings: IIamSettings, userName: string, id: string): Promise<User | null | undefined>;
    isKeycloakDiscoverySucceedInTime(iamSettings: IIamSettings, userName: string, id: string): Promise<IamOptimisticRefreshStatus>;
    getUsername(): Promise<string | undefined>;
    login(appState: IIamAppState, username: string): Promise<void>;
    destroy(isIamProviderSignout: boolean): Promise<void>;
    silentSignIn(api: DriveApi, id: string): Promise<void>;
    handleSilentSignout(userName: string): Promise<void>;
    handleRedirectAndGetUser(userName: string): Promise<User | null>;
    signInAndChangePassword(iamSettings: IIamSettings, userName: string, signinPopupCallback?: PopupCallBackType): Promise<void>;
}

export function isIamCallback() {
    return location.search.includes("state=") && (location.search.includes("code=") || location.search.includes("error="));
}

export function isMyAdmin(claim: IdTokenClaims | undefined) {
    return claim && claim[MYADMIN_KEY] === "true";
}

export class IamService implements IIamService {
    private manager?: UserManager;
    private retriesBeforeSuccessfulKeycloakDiscovery = 0;

    constructor(private readonly store: DriveStore) {
    }

    init(iIamSettings: IIamSettings, storageKey: string, signinPopupCallback?: PopupCallBackType) {
        if (this.manager) {
            this.manager = undefined;
        }

        this.manager = getIamManager(iIamSettings, this.store, storageKey, signinPopupCallback);
    }

    async getErrorMessage(error, refreshTime: number, id: string, creds: ICredentialsWithServer, oldRefreshToken: string) {
        let errorMessage = "";
        if (constants.IAM.ERROR.find(e => error.message.includes(e))) {
            errorMessage = `iamService.silentSignIn: expiring session: ${ error }. Refresh request duration: ${ refreshTime  } seconds ${ id }`;

            if (creds.iamSettings && error.message.includes(constants.IAM.ERROR[0])) {
                // Log token stored in secure storage when Max refresh token reuse exceeded error is encountered.
                try {
                    const tokenInSecureStorage = await this.getRefreshTokenFromSecureStorage(creds);
                    tokenInSecureStorage && (errorMessage += `. Secure Storage ending with ${ tokenInSecureStorage.slice(-5) } vs local storage ending with ${ oldRefreshToken.slice(-5) }.`);
                } catch (_) {
                    // do nothing
                }
            }
        } else {
            errorMessage = `iamService.silentSignIn: ${ error }. Refresh request duration: ${ refreshTime } seconds ${ id }`;
        }
        return errorMessage;
    }

    async getUser(iamSettings: IIamSettings, userName: string, id: string) {
        if (!this.manager) {
            this.init(iamSettings, userName);
        }

        let user: User | null | undefined;
        try {
            user = await this.manager?.getUser();
        } catch (error) {
            logger.error(logger.Source.IAM, `IamService.getUser: Failed to get user: ${ error } ${ id }`, userName, error);
        }

        return user;
    }

    async shouldOptimisticRefresh(creds: ICredentialsWithServer, id: string, refreshOnLaunch = false): Promise<IamOptimisticRefreshStatus> {
        if (!creds?.iamSettings) {
            return IamOptimisticRefreshStatus.DO_NOT_REFRESH;
        }

        let user = await this.getUser(creds.iamSettings, creds.credentials.userName, id);
        if (!user) {
            return IamOptimisticRefreshStatus.DO_NOT_REFRESH;
        }

        let tokenExpiration = user.expires_at;
        if (!refreshOnLaunch && this.isNotWithinOptimisticRefreshWindow(tokenExpiration)) {
            return IamOptimisticRefreshStatus.DO_NOT_REFRESH;
        }

        let discoveryResponse = await this.isKeycloakDiscoverySucceedInTime(creds.iamSettings, creds.credentials.userName, id);
        return discoveryResponse;
    }

    async getClaim() {
        if (!this.manager) {
            throw new Error("iamService.getClaim: IAM not initialized");
        }

        const user = await this.manager.getUser();
        return user?.profile;
    }

    async isKeycloakDiscoverySucceedInTime(iamSettings: IIamSettings, userName: string, id: string): Promise<IamOptimisticRefreshStatus> {
        if (!this.manager) {
            this.init(iamSettings, userName);
        }
        if (!this.manager) {
            return IamOptimisticRefreshStatus.DO_NOT_REFRESH;
        }

        let keycloakBaseUrl: string | undefined = iamSettings.loginUrl;
        if (!keycloakBaseUrl) {
            const user = await this.manager.getUser();
            keycloakBaseUrl = user?.profile.iss;
        }

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), constants.IAM.OPTIMISTIC_API_MAX_DURATION);
        try {
            let result = await trackedFetch(`${ keycloakBaseUrl }/.well-known/openid-configuration`, { signal: controller.signal });
            if (result.status >= 500) {
                return IamOptimisticRefreshStatus.KEYCLOAK_SERVER_ERROR_ENCOUNTERED;
            }
        } catch (error) {
            if (error.name === "AbortError") {
                logger.error(
                    logger.Source.IAM,
                    `IamService.isKeycloakDiscoverySucceedInTime: API call took more than ${ constants.IAM.OPTIMISTIC_API_TIMEOUT / 1000 }s, optimistic token refresh aborted ${ id }`,
                    userName
                );
            } else {
                this.retriesBeforeSuccessfulKeycloakDiscovery++ < constants.IAM.RETRIES_BEFORE_DISABLING_NETWORK_ERROR_LOGGING &&
                    logger.error(logger.Source.IAM, `IamService.isKeycloakDiscoverySucceedInTime: ${ error }. Failed to time the API call ${ id }`, userName);
            }

            return IamOptimisticRefreshStatus.DO_NOT_REFRESH;
        } finally {
            clearTimeout(timeoutId);
        }

        return IamOptimisticRefreshStatus.PROCEED_TO_REFRESH;
    }

    async getUsername() {
        if (!this.manager) {
            throw new Error("iamService.getUsername: IAM not initialized");
        }

        const user = await this.manager.getUser();
        return user?.profile.email || user?.profile.preferred_username;
    }

    async login(appState: IIamAppState, username: string) {
        if (!this.manager) {
            throw new Error("iamService.login: IAM not initialized");
        }

        this.clearUnusedOidcObjectInStorage();
        let redirectUri = this.getLoginRedirectUri(username, appState.addCoDriver);

        return this.manager.signinRedirect({
            redirect_uri: redirectUri,
            login_hint: username,
            state: appState
        });
    }

    async destroy(isIamProviderSignout: boolean) {
        if (!this.manager) {
            return;
        }

        if (!isIamProviderSignout) {
            this.manager = undefined;
            return;
        }

        const tempManager = this.manager;
        let userName = await this.getUsername();
        this.manager = undefined;

        logger.log(logger.Source.IAM, "iamService.destroy: IAM user signing out", userName);
        try {
            await tempManager.revokeTokens(["access_token", "refresh_token"]);
            await tempManager.removeUser();
            return;
        } catch (error) {
            logger.error(logger.Source.IAM, `iamService.destroy: ${ error }`, userName, error);
            return;
        }
    }

    async silentSignIn(api: DriveApi, id = "") {
        const creds = api.getCurrentCredentials();
        if (!creds?.iamSettings) {
            return;
        }

        // should only need initializing on first call
        if (!this.manager) {
            this.init(creds.iamSettings, creds.credentials.userName);
        }

        if (!this.manager) {
            logger.error(logger.Source.IAM, `iamService.silentSignIn: IAM not initialized, iamSetting ${ JSON.stringify(creds.iamSettings) } ${ id }`, creds.credentials.userName);
            throw new IamSilentSignInError(`IAM not initialized`);
        }

        // During the IAM login flow, we create a new context which is missing the user object
        // we should try to the load the user object and see if we actually need to refresh (we shouldn't during the IAM Login flow)
        // this would prevent refreshing during the IAM login flow right before the redirected which could cause refresh token usage exceeded.
        let oldUser = await this.manager.getUser();
        const oldRefreshTokenInLocalStorage = oldUser && oldUser.refresh_token ? oldUser.refresh_token  : "";
        const oldRefreshTokenInSecureStorage = await this.getRefreshTokenFromSecureStorage(creds);

        logger.log(
            logger.Source.IAM,
            `iamService.silentSignIn: refreshing using: ${ oldRefreshTokenInLocalStorage.slice(-5) }${ oldRefreshTokenInSecureStorage ? ", from secure storage: " + oldRefreshTokenInSecureStorage.slice(-5) : "" }. ${ id }`,
            creds.credentials.userName
        );

        let credentials = creds.credentials;
        let user: User | null;
        let startRefreshTime: DOMHighResTimeStamp = performance.now();
        let refreshTime: DOMHighResTimeStamp;

        if (!this.checkIfLocalStorageIsAvailable()) {
            let localStorageError = new Error("Local storage is not available. Aborting refresh.");
            if (shouldAddExtraLogging(logger.Source.IAM)) {
                sendToErrorTracker(localStorageError, "IAM");
            }
            throw localStorageError;
        }

        try {
            user = await this.manager.signinSilent({ silentRequestTimeoutInSeconds: constants.IAM.SILENT_REFRESH_TIMEOUT_SECONDS });
            if (!user) {
                throw new Error("User return null");
            }
        } catch (error) {
            refreshTime = Math.round((performance.now() - startRefreshTime) / 1000);
            let errorMessage = await this.getErrorMessage(error, refreshTime, id, creds, oldRefreshTokenInLocalStorage);

            if (shouldAddExtraLogging(logger.Source.IAM)) {
                sendToErrorTracker(error, "IAM");
            }
            logger.error(logger.Source.IAM, errorMessage, credentials.userName);
            throw error;
        }

        refreshTime = Math.round((performance.now() - startRefreshTime) / 1000);
        const newRefreshedTokenInLocalStorage = user.refresh_token ? user.refresh_token  : "";
        const newRefreshedTokenInSecureStorage = await this.getRefreshTokenFromSecureStorage(creds);
        logger.log(
            logger.Source.IAM,
            `iamService.silentSignIn: Successfully refreshed. Received: ${ newRefreshedTokenInLocalStorage.slice(-5) },${ newRefreshedTokenInSecureStorage ? ", from secure storage: " + newRefreshedTokenInSecureStorage.slice(-5) : "" }. Refresh request duration: ${ refreshTime } seconds. ${ id }`,
            creds.credentials.userName
        );

        api.setHeaders(([
            { name: "Authorization", value: `Bearer ${ user.access_token }` },
            { name: "UserName", value: credentials.userName },
            { name: "Database", value: credentials.database }
        ]));
        let newCreds = {...creds.credentials, iamJwt: user.access_token};
        api.setUser(newCreds, creds.server, creds.iamSettings);
        this.retriesBeforeSuccessfulKeycloakDiscovery = 0;
    }

    async handleSilentSignout(userName: string) {
        if (!this.manager) {
            logger.error(logger.Source.IAM, "iamService.handleSilentSignout: IAM not initialized", userName);
            return;
        }

        const tempManager = this.manager;
        this.manager = undefined;

        try {
            await tempManager.revokeTokens(["access_token", "refresh_token"]);
            await tempManager.removeUser();
            return;
        } catch (error) {
            logger.error(logger.Source.IAM, `iamService.handleSilentSignout: ${ error }`, userName, error);
            return;
        }
    }

    async handleRedirectAndGetUser(userName: string) {
        if (!this.manager) {
            logger.error(logger.Source.IAM, "iamService.handleRedirectAndGetUser: IAM not initialized", userName);
            window.history.replaceState({}, document.title, `.${ window.location.hash }`);
            return null;
        }

        try {
            const user = await this.manager.signinRedirectCallback();

            let appState = user.state as IIamAppState;
            let addCoDriver = appState && appState.addCoDriver;
            let databaseFromUri = getDatabaseNameFromUri(window.location) + "/";
            let url = addCoDriver ? databaseFromUri : "";
            window.history.replaceState({}, document.title, `/${ url }drive/${ addCoDriver ? IamConstants.UrlHashWithAddCoDriver : IamConstants.UrlHash }`);
            return user;
        } catch (e) {
            logger.error(logger.Source.IAM, `iamService.handleRedirectAndGetUser: IAM redirect callback failed: ${ e }`, userName, e);
            window.history.replaceState({}, document.title, `${ window.location.hash }`);
            return null;
        }
    }

    async signInAndChangePassword(iamSettings: IIamSettings, username: string, signinPopupCallback?: PopupCallBackType) {
        // should only need initializing on first call
        this.init(iamSettings, username, signinPopupCallback ?? this.createPopup.bind(this));

        await this.manager?.signinRedirect({
            login_hint: username,
            redirect_uri: this.getLoginRedirectUri(username, false),
            extraQueryParams: {
                kc_action: "UPDATE_PASSWORD"
            }
        });
    }

    private async getRefreshTokenFromSecureStorage(creds: ICredentialsWithServer): Promise<string | undefined> {
        if (creds.iamSettings) {
            let iamUserKey = `${ creds.credentials.userName }user:${ creds.iamSettings.loginUrl }:${ creds.iamSettings.clientId }`;
            try {
                const iamCredFromSecureStorage = await getItem<IIdDictionary<IamCredentials>>("@store_iamCredentials", true);
                if (iamCredFromSecureStorage) {
                    let storedUser = JSON.parse(iamCredFromSecureStorage[iamUserKey].user);
                    return storedUser.refresh_token ? storedUser.refresh_token : undefined;
                }
            } catch (_err) {
                return undefined;
            }
        }
        return undefined;
    }

    /**
     * Check if token is not within the optimistic refresh window.
     *
     * @param tokenExpiration The token expiration time taken from the UserManager user object.
     *                        This value is not adjusted for clock drift, so we should not compare it with getServerDate().
     * @returns if the token is not within the optimistic refresh window
     */
    private isNotWithinOptimisticRefreshWindow (tokenExpiration: number | undefined) {
        return tokenExpiration && tokenExpiration > (Date.now() + constants.IAM.OPTIMISTIC_REFRESH_WINDOW) / 1000;
    }

    private checkIfLocalStorageIsAvailable() {
        const storage = _originalLocalStorage;
        const testKey = "__test__";

        let value = storage.getItem(testKey);
        if (value && value === "1") {
            storage.removeItem(testKey);
        }

        storage.setItem(testKey, "1");
        value = storage.getItem(testKey);
        if (value && value === "1") {
            storage.removeItem(testKey);
            return true;
        }

        return false;
    }

    private createPopup(samlLoginUrl: string) {
        return new Promise(() => {
            let samlCallBack = (() => {});

            if (window.geotabModules?.sso) {
                if (shouldLoginWithAS()) {
                    window.geotabModules?.sso.samlLoginWithAS({
                                samlLoginUrl: samlLoginUrl,
                                ephemeralSession: true
                        }, samlCallBack);
                } else {
                    window.geotabModules.sso.samlLogin({samlLoginUrl: samlLoginUrl}, samlCallBack);
                }
            }
        });
    }

    private getLoginRedirectUri(username: string, addCoDriver: boolean) {
        let iamLoginMethod = `iamLoginMethod=${ shouldLoginWithAS() ? "AS" : "SSO" }&`;
        // Forward the native shell's brand deep-link scheme so iamLogin.ts hands control
        // back to the running brand instead of a hardcoded geotabdrive:// (MYG-140338).
        // Added to the redirect_uri (not the outer auth URL) so it survives the Keycloak
        // round-trip and stays part of the redirect_uri oidc-client-ts reuses for the
        // token exchange. Omitted when absent (web / Cordova / older native shells), where
        // iamLogin.ts keeps its geotabdrive:// fallback.
        let scheme = window.device?.scheme;
        let callbackScheme = scheme ? `callbackScheme=${ encodeURIComponent(scheme) }&` : "";
        let redirect = window.geotabModules?.sso ? `/drive/iamLogin.html?${ iamLoginMethod }${ callbackScheme }` : "/drive/default.html?";
        let urlHash = addCoDriver ? IamConstants.UrlHashWithAddCoDriver : IamConstants.UrlHash;
        return `${ window.location.origin }${ redirect }${ IamConstants.UsernameSearchParam }${ encodeURIComponent(username) }${ urlHash }`;
    }

    private clearUnusedOidcObjectInStorage() {
        for (let i = 0; i < _originalLocalStorage.length; i++) {
            let key = _originalLocalStorage.key(i);
            if (key && key.startsWith("oidc.")) {
                _originalLocalStorage.removeItem(key);
            }
        }
    }
}
